import React from 'react';
import {observer} from 'mobx-react';
import {Link, useNavigate, Navigate} from 'react-router-dom';
import {Box, Button, Container, Stack} from '@mui/material';
import styled from '@emotion/styled';
import {Checkbox, ErrorText, Label} from 'views/components/form';
import {Exist} from 'views/components/exist/exist.component';
import {useViewController} from 'data/services/locator';
import {Bindings} from 'data/constants/bindings';
import {modalSponsorLogo, sponsorLink} from 'data/constants/header';
import {SYNDICATE, LINKS} from 'data/constants';
import {SyndicatesType} from 'data/enums';
import {HocLayout} from 'views/components/hoc_layout/hoc_layout.component';
import {SponsorWrapper} from 'views/components/style';
import NflLogo from 'assets/img/icons/modal/modalLogo.svg?react';
import {media} from 'assets/css/media';
import {IRegistrationTermsController} from 'views/pages/registartion_terms/registration_terms.controller';

const Form = styled("form")`
    width: 100%;
    max-width: 486px;
    padding: 150px 20px 20px 20px;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    background: #fff;
    border-radius: 8px;
    margin: 90px auto 60px;
    color: #000;
    position: relative;

    h2 {
        color: var(--secondaryColor);
        text-align: center;
        font-family: var(--fontFamilyBase);
        font-size: 40px;
        font-style: normal;
        font-weight: 900;
        line-height: 34px; /* 85% */
        text-transform: capitalize;
    }

    h5 {
        color: #151515;
        text-align: center;
        font-family: var(--fontFamilyBase);
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 140%; /* 22.4px */
    }

    button {
        background: var(--primaryColor);
        width: max-content;
        margin: 0 auto;
    }
`;
const LogoContainer = styled(Box)`
	position: absolute;
	top: -110px;
	left: 50%;
	transform: translateX(-50%);
	width: 300px;

	svg {
		width: 100%;
		height: auto;
	}
`;

const SLink = styled(Link)`
    color: var(--primaryColor);
`;

const SponsorIcon = styled.img`
    height: 32px;
    width: auto;
    object-fit: contain;

    @media (min-width: ${media.tablet}) {
        height: 40px;
    }

    &.${SyndicatesType.CA}, &.${SyndicatesType.DE} {
        height: 24px;
    }
`;

export const RegistrationTermsPage: React.FC = observer(() => {
	const {
		i18n,
		error,
		isFormDisabledByData,
		handleFormSubmit,
		handleFormOnChange,
		handleCheckboxChange,
		hasAcceptedTerms
	} = useViewController<IRegistrationTermsController>(Bindings.RegistrationTermsController, {
		navigate: useNavigate(),
	});


	if( hasAcceptedTerms) {
		return (
			<Navigate to={'/'} replace={true}/>
		)
	}

	return (
		<HocLayout withBg={true}>
			<Container
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}>
				<Form onSubmit={handleFormSubmit} onChange={handleFormOnChange}>
					<LogoContainer>
						<NflLogo width="128px" height="128px"/>
					</LogoContainer>

					<Stack direction="column" gap={3}>
						{modalSponsorLogo && (
							<SponsorWrapper link={sponsorLink}>
								<SponsorIcon className={SYNDICATE} src={modalSponsorLogo}/>
							</SponsorWrapper>
						)}

						<Stack direction="column" gap={2.5} position={'relative'} zIndex={2}>
							<Stack direction="row" gap={2}>
								<Checkbox
									name="terms"
									id="terms"
									onChange={handleCheckboxChange}
									required={true}
								/>
								<Label htmlFor="terms">
									{i18n.t(
										"registration_terms.terms.label_1",
										"By selecting this box I agree "
									)}
									<SLink to={LINKS.terms}>
										{i18n.t(
											"registration_terms.terms.label_2",
											"Terms and Conditions"
										)}
									</SLink>
									{"*"}
								</Label>
							</Stack>
							<Exist when={error?.terms}>
								<ErrorText>{error?.terms}</ErrorText>
							</Exist>
						</Stack>

						<Button
							type="submit"
							variant="contained"
							size="large"
							disabled={isFormDisabledByData}>
							{i18n.t("registration_terms.form.submit", "Start picking")}
						</Button>
					</Stack>
				</Form>
			</Container>
		</HocLayout>
	);
});

export default RegistrationTermsPage;
